import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Linkedin, ExternalLink, CheckCircle2, Clock, AlertTriangle, RefreshCw,
  Trash2, Loader2, Building2, Megaphone, Ban, Link2, Layers,
} from "lucide-react";

const RES_SERVICES = {
  linkedin_ad_account: { label: "Ad accounts", icon: Megaphone, color: "#0A66C2" },
  linkedin_page: { label: "Company pages", icon: Building2, color: "#0A66C2" },
};

export default function ClientLinkedInPage() {
  const { clientId } = useParams();
  const [connection, setConnection] = useState({ connected: false });
  const [adAccounts, setAdAccounts] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [oauthing, setOauthing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [assignments, setAssignments] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a, o, bu, asg] = await Promise.all([
        api.get(`/clients/${clientId}/linkedin/connection`).then((r) => r.data),
        api.get(`/clients/${clientId}/linkedin/ad-accounts`).then((r) => r.data).catch(() => []),
        api.get(`/clients/${clientId}/linkedin/organizations`).then((r) => r.data).catch(() => []),
        api.get(`/clients/${clientId}/business-units`).then((r) => r.data).catch(() => []),
        api.get(`/clients/${clientId}/linkedin/resource-assignments`)
          .then((r) => r.data).catch(() => ({ assignments: {} })),
      ]);
      setConnection(c);
      setAdAccounts(a || []);
      setOrgs(o || []);
      setBusinessUnits(bu || []);
      setAssignments(asg?.assignments || {});
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const startOAuth = async (appType = "ads") => {
    setOauthing(true);
    try {
      const redirectUri = `${window.location.origin}/integrations/linkedin/callback`;
      const { data } = await api.post(`/clients/${clientId}/linkedin/oauth/start`,
                                      { redirect_uri: redirectUri, app_type: appType });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start LinkedIn OAuth");
    } finally { setOauthing(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/linkedin/sync/hierarchy`);
      toast.success(
        `Synced ${data.ad_accounts} ad accounts · ${data.organizations} orgs · ${data.campaigns} campaigns`);
      await refresh();
    } catch (e) {
      const msg = e.response?.data?.detail || "Sync failed";
      if (e.response?.status === 504) {
        toast.warning("LinkedIn was slow — retry in a minute. Partial results saved.");
      } else if (msg.toLowerCase().includes("not approved") || msg.includes("403")) {
        toast.warning("MDP scopes not yet approved by LinkedIn — see docs/linkedin_mdp_setup.md");
      } else {
        toast.error(msg);
      }
    } finally { setSyncing(false); }
  };

  const disconnect = async (appType = "all") => {
    const label = appType === "pages" ? "Pages app"
                : appType === "ads"   ? "Ads app"
                : "LinkedIn";
    if (!window.confirm(`Disconnect ${label} for this client?`)) return;
    try {
      await api.delete(`/clients/${clientId}/linkedin/disconnect`,
        { params: { app_type: appType } });
      toast.success(`${label} disconnected`); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const assignResource = useCallback(async (service, resource_id, resource_label,
                                            business_unit_id, status) => {
    const key = `${service}::${resource_id}`;
    // Optimistic update
    setAssignments((prev) => {
      const next = { ...prev };
      if (status === "unassigned") {
        delete next[key];
      } else {
        next[key] = { service, resource_id, resource_label,
                      business_unit_id: status === "assigned" ? business_unit_id : null,
                      status };
      }
      return next;
    });
    try {
      await api.post(`/clients/${clientId}/linkedin/resource-assignments`, {
        service, resource_id, resource_label,
        business_unit_id: status === "assigned" ? business_unit_id : null,
        status,
      });
      toast.success(
        status === "assigned" ? "Assigned to property"
          : status === "ignored" ? "Ignored — excluded from reports"
          : "Cleared",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
      // Rollback via authoritative refetch
      try {
        const { data } = await api.get(`/clients/${clientId}/linkedin/resource-assignments`);
        setAssignments(data?.assignments || {});
      } catch (_) { /* ignore */ }
    }
  }, [clientId]);

  // Merge resources into rows for the delegation table
  const resourceRows = useMemo(() => {
    const rows = [];
    for (const a of adAccounts) {
      rows.push({
        service: "linkedin_ad_account",
        resource_id: a.linkedin_ad_account_id || a.id,
        resource_label: a.name || a.linkedin_ad_account_id,
        meta: a.currency ? `${a.currency} · ${a.status || "—"}` : (a.status || "—"),
      });
    }
    for (const o of orgs) {
      rows.push({
        service: "linkedin_page",
        resource_id: o.organization_urn || o.id,
        resource_label: o.display_name || o.name || o.organization_urn,
        meta: o.role || "—",
      });
    }
    return rows;
  }, [adAccounts, orgs]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of resourceRows) {
      g[r.service] = g[r.service] || [];
      g[r.service].push(r);
    }
    return g;
  }, [resourceRows]);

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="linkedin-page">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#0A66C2] text-white">
          <Linkedin className="h-5 w-5" />
        </div>
        <div>
          <div className="label-overline">LINKEDIN ADS + PAGES</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">LinkedIn</h1>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            Connect this client&apos;s LinkedIn account and import Ads + Company Page metrics into reports and charts.
            Until the agency&apos;s Marketing Developer Platform (MDP) application is approved by LinkedIn, the OAuth
            flow works but the sync endpoints will return <code className="font-mono text-[11px]">403</code>.
          </p>
        </div>
      </div>

      {/* MDP banner */}
      <Card className="rounded-sm border-amber-200 bg-amber-50" data-testid="linkedin-mdp-banner">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-[12px] text-amber-900 flex-1">
            <div className="font-semibold mb-0.5">Marketing Developer Platform approval required</div>
            <div>
              LinkedIn manually approves all Marketing API access. Until then, keep using CSV uploads — the
              <span className="font-mono mx-1 px-1 bg-amber-100 rounded-sm">linkedin_ads</span> and
              <span className="font-mono mx-1 px-1 bg-amber-100 rounded-sm">linkedin_pages</span> platform keys
              are already enabled on Properties &amp; Uploads. See
              <a href="/docs/linkedin_mdp_setup.md" className="underline ml-1 inline-flex items-center gap-0.5"
                target="_blank" rel="noreferrer">
                the setup guide <ExternalLink className="h-3 w-3" />
              </a>
              for the application steps.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ads app connection */}
      <Card className="rounded-sm border-zinc-200" data-testid="linkedin-ads-conn">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-overline">App 1 · Advertising + OIDC</div>
              <div className="font-display font-bold text-lg mt-0.5">Ads &amp; login connection</div>
            </div>
            {connection.connected ? (
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Connected
              </Badge>
            ) : (
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200">
                Not connected
              </Badge>
            )}
          </div>
          {connection.connected ? (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-bold text-lg">{connection.linkedin_member_name || "—"}</div>
                  <div className="text-[11px] font-mono text-zinc-500 break-all">
                    {connection.linkedin_member_email} · scopes: {connection.scopes || "—"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-sm"
                    disabled={syncing} onClick={sync} data-testid="linkedin-sync">
                    {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                             : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                    Sync now
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                    onClick={() => disconnect("ads")} data-testid="linkedin-disconnect">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Disconnect Ads
                  </Button>
                </div>
              </div>
              {connection.last_error && (
                <div className="rounded-sm border border-red-200 bg-red-50 p-2.5 text-[11px] font-mono text-red-800">
                  {connection.last_error}
                </div>
              )}
              {connection.last_sync_completed_at && (
                <div className="text-[11px] text-zinc-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Last sync:{" "}
                  {new Date(connection.last_sync_completed_at).toLocaleString()}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-zinc-100">
                <div>
                  <div className="label-overline mb-1.5">Ad accounts ({adAccounts.length})</div>
                  {adAccounts.length === 0 ? (
                    <div className="text-[11px] italic text-zinc-500">None synced yet.</div>
                  ) : adAccounts.slice(0, 10).map((a) => (
                    <div key={a.linkedin_ad_account_id} className="font-mono text-[11px] py-0.5 truncate">
                      {a.name || "—"} <span className="text-zinc-400">· {a.linkedin_ad_account_id}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="label-overline mb-1.5">Company pages ({orgs.length})</div>
                  {orgs.length === 0 ? (
                    <div className="text-[11px] italic text-zinc-500">None synced yet.</div>
                  ) : orgs.slice(0, 10).map((o) => (
                    <div key={o.organization_urn} className="font-mono text-[11px] py-0.5 truncate">
                      {o.organization_urn} <span className="text-zinc-400">· {o.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="label-overline">Connect this client&apos;s LinkedIn account</div>
              <p className="text-[12px] text-zinc-600">
                Clicking Connect redirects to LinkedIn for the OAuth consent screen. Tokens are stored
                encrypted server-side and never exposed to the browser after save.
              </p>
              <Button onClick={() => startOAuth("ads")} disabled={oauthing}
                className="rounded-sm bg-[#0A66C2] hover:bg-[#084d92] text-white"
                data-testid="linkedin-connect">
                {oauthing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          : <Linkedin className="h-3.5 w-3.5 mr-1.5" />}
                Connect Ads app
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pages app connection — Community Management API */}
      <Card className="rounded-sm border-zinc-200" data-testid="linkedin-pages-conn">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-overline">App 2 · Community Management</div>
              <div className="font-display font-bold text-lg mt-0.5">Company Pages connection</div>
              <p className="text-[11px] text-zinc-500 mt-1 max-w-xl">
                Second OAuth grant against the dedicated Community
                Management API app. Required to pull organic Company
                Page posts &amp; follower analytics into the report.
              </p>
            </div>
            {connection.pages_connected ? (
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200"
                data-testid="linkedin-pages-connected-badge">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Connected
              </Badge>
            ) : (
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200">
                Not connected
              </Badge>
            )}
          </div>
          {connection.pages_connected ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-bold text-lg">{connection.pages_member_name || "—"}</div>
                <div className="text-[11px] font-mono text-zinc-500 break-all">
                  {connection.pages_member_email} · scopes: {connection.pages_scopes || "—"}
                </div>
                {connection.pages_connected_at && (
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Connected {new Date(connection.pages_connected_at).toLocaleString()}
                  </div>
                )}
              </div>
              <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                onClick={() => disconnect("pages")}
                data-testid="linkedin-pages-disconnect">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Disconnect Pages
              </Button>
            </div>
          ) : (
            <>
              <p className="text-[12px] text-zinc-600">
                {connection.connected
                  ? "Ads app is connected. Now authorise the Community Management API on top to unlock organic Page posts."
                  : "You can connect the Pages app on its own, or run the two OAuth rounds sequentially after connecting the Ads app above."}
              </p>
              <Button onClick={() => startOAuth("pages")} disabled={oauthing}
                className="rounded-sm bg-[#0A66C2] hover:bg-[#084d92] text-white"
                data-testid="linkedin-pages-connect">
                {oauthing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          : <Linkedin className="h-3.5 w-3.5 mr-1.5" />}
                Connect Pages app
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Property Delegation — mirrors /clients/:id/google-feeds */}
      {connection.connected && (
        <PropertyDelegationSection
          grouped={grouped}
          businessUnits={businessUnits}
          assignments={assignments}
          onAssign={assignResource}
          onRefresh={sync}
          syncing={syncing}
          clientId={clientId} />
      )}
    </div>
  );
}

// ── Property delegation card + row picker ──────────────────────────────
function PropertyDelegationSection({ grouped, businessUnits, assignments,
                                     onAssign, onRefresh, syncing, clientId }) {
  const services = Object.keys(RES_SERVICES);
  const totalRes = services.reduce((n, s) => n + (grouped[s]?.length || 0), 0);
  const assignedCount = Object.values(assignments).filter(
    (a) => a?.status === "assigned").length;
  const ignoredCount = Object.values(assignments).filter(
    (a) => a?.status === "ignored").length;

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="linkedin-property-delegation">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-overline">Property delegation</div>
            <h2 className="font-display font-bold text-xl mt-0.5 flex items-center gap-2">
              <Layers className="h-5 w-5 text-[#0A66C2]" />
              Attach LinkedIn resources to properties
            </h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-3xl">
              Assign each ad account and company page to one of this client&apos;s
              properties (business units). Reports for that property will only
              include LinkedIn data from the resources you attach here. Mark a
              resource as <span className="font-semibold">Ignored</span> to
              exclude it entirely — useful when a personal ad account was
              granted access alongside the client&apos;s.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">
              {assignedCount} assigned
            </Badge>
            <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 text-[10px]">
              {totalRes - assignedCount - ignoredCount} unassigned
            </Badge>
            <Badge className="rounded-sm bg-red-50 text-red-800 border-red-200 text-[10px]">
              {ignoredCount} ignored
            </Badge>
            <Button size="sm" variant="outline" onClick={onRefresh}
              disabled={syncing}
              className="rounded-sm" data-testid="linkedin-delegation-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Refresh from LinkedIn"}
            </Button>
          </div>
        </div>

        {businessUnits.length === 0 && (
          <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              This client has no properties yet — create at least one in the
              <a href={`/clients/${clientId}/properties`}
                 className="underline mx-1 font-medium">Properties</a>
              tab before assigning LinkedIn resources.
            </div>
          </div>
        )}

        {services.map((svc) => {
          const cfg = RES_SERVICES[svc];
          const rows = grouped[svc] || [];
          const Icon = cfg.icon;
          return (
            <div key={svc} className="space-y-2"
              data-testid={`linkedin-delegation-${svc}`}>
              <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
                <Icon className="h-4 w-4" style={{ color: cfg.color }} />
                <span className="font-display font-bold text-sm">{cfg.label}</span>
                <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 text-[10px]">
                  {rows.length}
                </Badge>
              </div>
              {rows.length === 0 ? (
                <div className="text-[11px] italic text-zinc-500 pl-6">
                  Nothing synced yet — click <b>Refresh from LinkedIn</b> above
                  (requires MDP scope approval).
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                        <th className="py-1.5 pr-3">Name</th>
                        <th className="py-1.5 pr-3">ID / URN</th>
                        <th className="py-1.5 pr-3 w-56">Property</th>
                        <th className="py-1.5 pr-3 w-32">Status</th>
                        <th className="py-1.5 w-24" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <ResourceAssignmentRow
                          key={`${r.service}::${r.resource_id}`}
                          row={r}
                          businessUnits={businessUnits}
                          assignment={assignments[`${r.service}::${r.resource_id}`]}
                          onAssign={onAssign} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ResourceAssignmentRow({ row, businessUnits, assignment, onAssign }) {
  const status = assignment?.status || "unassigned";
  const buId = assignment?.business_unit_id || "";
  const isIgnored = status === "ignored";
  const isAssigned = status === "assigned";
  return (
    <tr className={`border-b border-zinc-100 ${isIgnored ? "opacity-60" : ""}`}
      data-testid={`linkedin-res-row-${row.resource_id}`}>
      <td className="py-2 pr-3 font-medium truncate max-w-[280px]" title={row.resource_label}>
        {row.resource_label}
      </td>
      <td className="py-2 pr-3 font-mono text-zinc-500 text-[10px] truncate max-w-[220px]"
        title={row.resource_id}>
        {row.resource_id}
      </td>
      <td className="py-2 pr-3">
        <Select
          value={isAssigned ? buId : "__none"}
          onValueChange={(v) => {
            if (v === "__none") {
              onAssign(row.service, row.resource_id, row.resource_label, null, "unassigned");
            } else {
              onAssign(row.service, row.resource_id, row.resource_label, v, "assigned");
            }
          }}
          disabled={isIgnored || businessUnits.length === 0}>
          <SelectTrigger className="h-7 text-xs rounded-sm"
            data-testid={`linkedin-res-bu-${row.resource_id}`}>
            <SelectValue placeholder="— Not assigned —" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">— Not assigned —</SelectItem>
            {businessUnits.map((bu) => (
              <SelectItem key={bu.business_unit_id} value={bu.business_unit_id}>
                {bu.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-2 pr-3">
        {status === "assigned" && (
          <Badge className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">
            <Link2 className="h-3 w-3 mr-1" /> Assigned
          </Badge>
        )}
        {status === "ignored" && (
          <Badge className="rounded-sm bg-red-50 text-red-800 border-red-200 text-[10px]">
            <Ban className="h-3 w-3 mr-1" /> Ignored
          </Badge>
        )}
        {status === "unassigned" && (
          <Badge className="rounded-sm bg-zinc-100 text-zinc-600 border-zinc-200 text-[10px]">
            Unassigned
          </Badge>
        )}
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        {isIgnored ? (
          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]"
            onClick={() => onAssign(row.service, row.resource_id, row.resource_label,
                                    null, "unassigned")}
            data-testid={`linkedin-res-restore-${row.resource_id}`}>
            Restore
          </Button>
        ) : (
          <Button size="sm" variant="ghost"
            className="h-6 px-1.5 text-[10px] text-red-600 hover:text-red-700"
            onClick={() => onAssign(row.service, row.resource_id, row.resource_label,
                                    null, "ignored")}
            data-testid={`linkedin-res-ignore-${row.resource_id}`}>
            <Ban className="h-3 w-3 mr-1" /> Ignore
          </Button>
        )}
      </td>
    </tr>
  );
}
